import { subscriptionPrices } from "@arbeidsark/shared"
import type { StripeSubscription } from "@arbeidsark/shared"
import { useState } from "react"
import styled from "styled-components"
import { useTranslate } from "~/ur-react-hooks"
import { format } from "~/utils/date-fns/format"
import { trpc } from "~/utils/trpc"
import WillCancelPrompt from "../components/WillCancelPrompt"

const Layout = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 6rem;
`

const Card = styled.div`
  background: white;
  box-shadow: ${(props) => props.theme.shadow.elevation3};
  padding: 2rem;
  width: 456px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 8px;
`

const Title = styled.h3`
  margin: 0;
`

const Subtitle = styled.p`
  margin: 0;
  margin-top: 1rem;
`

const WillCancelText = styled.p`
  text-align: center;
  margin-bottom: 0;
`

const Reactivate = styled.button`
  ${(props) => props.theme.button.reset}
  color: ${(props) => props.theme.colors.secondary50};
  margin-top: 1.375rem;
  text-decoration: underline;
`

const Price = styled.div`
  margin-top: 1rem;
  font-size: 1.5rem;
`

export interface PersonalSubscriptionWillCancelProps {
  subscription: StripeSubscription
}

const PersonalSubscriptionWillCancel: React.FC<
  PersonalSubscriptionWillCancelProps
> = ({ subscription }) => {
  const [isPromptOpen, setIsPromptOpen] = useState(false)

  const nextPaymentFormatted = format(
    subscription.nextPayment ?? new Date(),
    "dd.MM.yyyy",
  )
  const translations = useTranslate({
    subscriptionTrialTitle: "subscription-personal.trial-title",
    subscriptionRegularTitle: "subscription-personal.regular-title",
    subscriptionWillCancelSubtitle: [
      "subscription-personal.will-cancel-subtitle",
      { date: nextPaymentFormatted },
    ],
    price: ["subscription-personal.price", { price: "299" }],
    willCancel: "subscription-personal.will-cancel",
    reactivate: "subscription-personal.reactivate",
  })

  const trpcContext = trpc.useContext()

  const { mutate: reactivateSubscription, isLoading } =
    trpc.subscriptions.reactivateSubscription.useMutation({
      onSuccess() {
        trpcContext.users.me.invalidate()
      },
    })

  const isTrialing = subscription.status === "trialing"
  const subscriptionPeriodicity = subscription.periodicity
  const price = subscriptionPrices[subscriptionPeriodicity] / 100

  return (
    <Layout>
      <Card>
        <Title>
          {isTrialing
            ? translations.subscriptionTrialTitle
            : translations.subscriptionRegularTitle}
        </Title>
        <Price>{translations.price({ price: price.toString() })}</Price>
        <Subtitle>{translations.subscriptionWillCancelSubtitle()}</Subtitle>
        <WillCancelText>{translations.willCancel}</WillCancelText>
        <Reactivate disabled={isLoading} onClick={() => setIsPromptOpen(true)}>
          {translations.reactivate}
        </Reactivate>
        <WillCancelPrompt
          isOpen={isPromptOpen}
          onClose={(value) => {
            setIsPromptOpen(false)
            if (value) {
              reactivateSubscription({ id: subscription.stripeSubscriptionId })
            }
          }}
        />
      </Card>
    </Layout>
  )
}

export default PersonalSubscriptionWillCancel
